import {
  faBluesky,
  faFacebookF,
  faGoogle,
  faInstagram,
  faLinkedin,
  faLinktree,
  faPinterestP,
  faQuora,
  faTelegram,
  faThreads,
  faTiktok,
  faTumblr,
  faVimeoV,
  faWhatsapp,
  faXTwitter,
  faYelp,
  faYoutube,
} from '@awesome.me/kit-4a6bf60416/icons/classic/brands';
import { faF, faJ, faL, faM, faN, faS } from '@awesome.me/kit-4a6bf60416/icons/classic/solid';
import type { IconDefinition, IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { faAvvo, faLii } from '@/lib/custom-icons';
import { cn } from '@/lib/utils';

export type SocialLink = {
  platform: string;
  url: string;
};

export function socialLinkKey(index: number, link: Pick<SocialLink, 'platform' | 'url'>) {
  return `${index}:${link.platform}:${link.url}`;
}

// Rails keeps its own copy of these names and addresses, so a network added here
// has to be added there too, or imported links will not be recognised.
export const SUPPORTED_SOCIAL_PLATFORMS = [
  'youtube',
  'instagram',
  'linkedin',
  'x',
  'tiktok',
  'threads',
  'facebook',
  'whatsapp',
  'avvo',
  'yelp',
  'google',
  'pinterest',
  'bluesky',
  'telegram',
  'vimeo',
  'linktree',
  'tumblr',
  'quora',
  'justia',
  'findlaw',
  'lii',
  'martindale',
  'superlawyers',
  'lawyers_com',
  'nolo',
  'lawyer_com',
] as const;

export type SupportedSocialPlatform = (typeof SUPPORTED_SOCIAL_PLATFORMS)[number];

const SOCIAL_PLATFORM_CONFIG = {
  youtube: { label: 'YouTube', hosts: ['youtube.com', 'youtu.be'], icon: faYoutube },
  instagram: { label: 'Instagram', hosts: ['instagram.com'], icon: faInstagram },
  linkedin: { label: 'LinkedIn', hosts: ['linkedin.com'], icon: faLinkedin },
  x: { label: 'X', hosts: ['x.com', 'twitter.com'], icon: faXTwitter },
  tiktok: { label: 'TikTok', hosts: ['tiktok.com'], icon: faTiktok },
  threads: { label: 'Threads', hosts: ['threads.net', 'threads.com'], icon: faThreads },
  facebook: { label: 'Facebook', hosts: ['facebook.com', 'fb.com'], icon: faFacebookF },
  whatsapp: { label: 'WhatsApp', hosts: ['wa.me', 'whatsapp.com'], icon: faWhatsapp },
  avvo: { label: 'Avvo', hosts: ['avvo.com'], icon: faAvvo },
  yelp: { label: 'Yelp', hosts: ['yelp.com', 'yelp.to'], icon: faYelp },
  google: {
    label: 'Google',
    hosts: ['g.page', 'business.google.com', 'maps.app.goo.gl'],
    icon: faGoogle,
  },
  pinterest: { label: 'Pinterest', hosts: ['pinterest.com', 'pin.it'], icon: faPinterestP },
  bluesky: { label: 'Bluesky', hosts: ['bsky.app'], icon: faBluesky },
  telegram: {
    label: 'Telegram',
    hosts: ['t.me', 'telegram.me', 'telegram.org'],
    icon: faTelegram,
  },
  vimeo: { label: 'Vimeo', hosts: ['vimeo.com'], icon: faVimeoV },
  linktree: { label: 'Linktree', hosts: ['linktr.ee', 'linktree.com'], icon: faLinktree },
  tumblr: { label: 'Tumblr', hosts: ['tumblr.com'], icon: faTumblr },
  quora: { label: 'Quora', hosts: ['quora.com'], icon: faQuora },
  justia: { label: 'Justia', hosts: ['justia.com'], icon: faJ },
  findlaw: { label: 'FindLaw', hosts: ['findlaw.com'], icon: faF },
  lii: { label: 'Cornell LII', hosts: ['law.cornell.edu'], icon: faLii },
  martindale: { label: 'Martindale-Hubbell', hosts: ['martindale.com'], icon: faM },
  superlawyers: { label: 'Super Lawyers', hosts: ['superlawyers.com'], icon: faS },
  // biome-ignore lint/style/useNamingConvention: platform slugs are wire values shared with Ruby
  lawyers_com: { label: 'Lawyers.com', hosts: ['lawyers.com'], icon: faL },
  nolo: { label: 'Nolo', hosts: ['nolo.com'], icon: faN },
  // biome-ignore lint/style/useNamingConvention: platform slugs are wire values shared with Ruby
  lawyer_com: { label: 'Lawyer.com', hosts: ['lawyer.com'], icon: faL },
} as const satisfies Record<
  SupportedSocialPlatform,
  { label: string; hosts: readonly string[]; icon: IconDefinition }
>;

export function getSocialPlatformLabel(platform: string) {
  const normalizedPlatform = normalizeSocialPlatform(platform);
  if (!normalizedPlatform) {
    return platform;
  }

  return SOCIAL_PLATFORM_CONFIG[normalizedPlatform].label;
}

export function normalizeSocialPlatform(
  platform: string | null | undefined,
): SupportedSocialPlatform | null {
  if (!platform) return null;

  const value = platform.trim().toLowerCase();
  if (value === 'twitter' || value === 'x-twitter') return 'x';
  if (
    value === 'google business' ||
    value === 'google_business' ||
    value === 'google-business' ||
    value === 'gmb' ||
    value === 'googleplus' ||
    value === 'google_plus' ||
    value === 'gplus'
  ) {
    return 'google';
  }
  if (value in SOCIAL_PLATFORM_CONFIG) return value as SupportedSocialPlatform;
  return null;
}

export function normalizeSocialUrl(value: string) {
  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return '';
  }

  if (/^https?:\/\//i.test(trimmedValue)) {
    return trimmedValue;
  }

  return `https://${trimmedValue.replace(/^\/+/, '')}`;
}

export function inferSocialPlatformFromUrl(url: string): SupportedSocialPlatform | null {
  const normalizedUrl = normalizeSocialUrl(url);
  if (!normalizedUrl) {
    return null;
  }

  try {
    const hostname = new URL(normalizedUrl).hostname.replace(/^www\./, '').toLowerCase();

    for (const platform of SUPPORTED_SOCIAL_PLATFORMS) {
      const matchesHost = SOCIAL_PLATFORM_CONFIG[platform].hosts.some(
        (host) => hostname === host || hostname.endsWith(`.${host}`),
      );

      if (matchesHost) {
        return platform;
      }
    }
  } catch {
    return null;
  }

  return null;
}

export function SocialMediaIcon({ platform, className }: { platform: string; className?: string }) {
  const normalizedPlatform = normalizeSocialPlatform(platform);
  if (!normalizedPlatform) return null;

  const { icon, label } = SOCIAL_PLATFORM_CONFIG[normalizedPlatform];

  return (
    <FontAwesomeIcon
      icon={icon as IconProp}
      className={cn('size-5 shrink-0 text-foreground', className)}
      title={label}
    />
  );
}
